import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
    Box,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { OurServices_IMG, Programs_IMG } from '../../Constants/ImageConstants';
import { COLORS } from '../../Constants/styles';
import AccentButton from '../Common/AccentButton';
import CustomTextButton from '../Common/CustomButton';

const programsContent = [
    {
        title: 'Sports',
        subtitle: 'Kids & Adults',
        description:
            'Explore our top-notch sports instruction and leagues for kids and adults including basketball, soccer, baseball and more.',
        image: Programs_IMG.kidsnadultsImage,
        sports: ['Soccer', 'Basketball', 'Gymnastics', 'Swimming', 'Diving', 'Water Polo'],
    },
    {
        title: 'Aquatics',
        subtitle: 'All Ages',
        description:
            'Learn to swim and enjoy aquatic fitness with professional guidance in our state-of-the-art facilities.',
        image: Programs_IMG.kidsnadults_2Image,
        sports: ['Swimming', 'Diving', 'Water Polo'],
    },
];

const ProgramSectionBlock = ({ data, reverse, index }: any) => (
    <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, delay: index * 0.2 }}
    >
        <Box maxWidth="xl" mx="auto" px={{ xs: 2, md: 1 }} py={{ xs: 2, md: 2 }}
            sx={{
                display: 'flex',
                flexDirection: {
                    xs: 'column',
                    md: reverse ? 'row-reverse' : 'row',
                },
                justifyContent: 'center',
                alignItems: 'stretch',
                gap: 4,
                mt: index !== 0 ? 6 : 0,
            }}
        >
            {/* Image */}
            <Box
                component="img"
                src={data.image}
                alt={data.title}
                sx={{
                    width: { xs: '100%', md: '45%' },
                    borderRadius: 3,
                    objectFit: 'cover',
                    height: { xs: 300, md: '100%' },
                    maxHeight: { md: 650 },
                }}
            />

            {/* Content */}
            <Box
                sx={{
                    backgroundColor: COLORS.white,
                    p: { xs: 3, md: 5 },
                    flex: 1,
                    borderRadius: 3,
                    boxShadow: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    maxHeight: { md: 650 },
                }}
            >
                <Typography variant="h5" fontWeight="bold" gutterBottom>{data.subtitle}</Typography>

                <Typography
                    variant="h3"
                    gutterBottom
                    sx={{ color: COLORS.pureBlack }}
                >
                    {data.title}
                </Typography>

                <Typography variant="body2" sx={{ mb: 2, color: COLORS.accentBlackTransparent }}>
                    {data.description}
                </Typography>

                <Box sx={{ pt: { xs: 4, md: 6 }, pb: 0 }}>
                    <Divider sx={{ mb: 4 }} />

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(3, 1fr)',
                            },
                            gap: 2,
                        }}
                    >
                        {data.sports.map((sport: string, i: number) => (
                            <motion.div
                                key={sport}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ delay: i * 0.1, duration: 0.5 }}
                            >
                                <List dense disablePadding>
                                    <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
                                        <ListItemIcon
                                            sx={{
                                                minWidth: '32px',
                                                mt: '2px',
                                                color: COLORS.accentDarkGreen,
                                                stroke: '#000',
                                                strokeWidth: 0.5,
                                                fill: 'none',
                                                '& path': {
                                                    stroke: 'black',
                                                    strokeWidth: 0.5,
                                                },
                                            }}
                                        >
                                            <TaskAltIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={sport}
                                            slotProps={{
                                                primary: {
                                                    fontWeight: 'bold',
                                                    textTransform: 'uppercase',
                                                    fontFamily: "'Big Shoulders Text', sans-serif",
                                                    color: COLORS.pureBlack,
                                                }
                                            }}
                                        />
                                    </ListItem>
                                </List>
                            </motion.div>
                        ))}
                    </Box>

                    <Divider sx={{ mt: 4, mb: 0 }} />
                </Box>

                <Box mt={3}>
                    <CustomTextButton label="All sports" href="/about" />
                </Box>
            </Box>
        </Box>
    </motion.div>
);

const ProgramsForEveryone: React.FC = () => {

    return (
        <>
            <Box
                sx={{
                    px: { xs: 2, md: 6 },
                    py: { xs: 6, md: 8 },
                    backgroundColor: COLORS.greyBG,
                }}
            >
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                >
                    <Typography
                        variant="h3"
                        align="center"
                        fontWeight="bold"
                        sx={{ textTransform: 'uppercase', mb: 6, color: COLORS.pureBlack }}
                    >
                        Programs for Everyone
                    </Typography>
                </motion.div>

                <Box>
                    {programsContent.map((item, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: (index + 1) * 0.3, duration: 0.6 }}
                        >
                            <ProgramSectionBlock
                                data={item}
                                reverse={index % 2 !== 0}
                                index={index}
                            />
                        </motion.div>
                    ))}
                </Box>
            </Box>

            {/* banner section   BannerSection style: image bg, text on image, no card */}
            <Box
                sx={{
                    position: 'relative',
                    backgroundImage: `url(${OurServices_IMG.bottom_banner})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    minHeight: { xs: '44vh', md: '60vh' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    overflow: 'hidden',
                    px: 2,
                }}
            >
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            display: 'inline-block',
                            backgroundColor: COLORS.accentLime,
                            color: COLORS.pureBlack,
                            px: 2,
                            py: 0.5,
                            borderRadius: '6px',
                            mb: 3,
                            transform: 'skewX(-8deg)',
                        }}
                    >
                        LOCATIONS
                    </Typography>

                    <Typography variant="h3" fontWeight="bold" sx={{ mb: 3, color: COLORS.white, textTransform: 'capitalize' }}>
                        Fall Youth Sports Classes Start in September!
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 2, fontSize: '1rem', color: COLORS.white }}>
                        Explore Gymnastics, Ninja, Soccer, Basketball, Volleyball, Tennis + More!
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 4, fontSize: '1rem', color: COLORS.white }}>
                        12-week Trimester   Starts in September.
                    </Typography>

                    <AccentButton label="Contact Us" href="/contact" />
                </motion.div>
            </Box>
        </>
    );
};

export default ProgramsForEveryone;
